import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { METRICS } from "@/data/content";

function CountUp({ value, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1600;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  return (
    <span ref={ref}>
      {n}
      {suffix}
    </span>
  );
}

export default function Metrics() {
  return (
    <section
      id="metrics"
      className="relative border-t border-white/10 bg-[#0c0c0c] py-24 md:py-40"
      data-testid="metrics-section"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="mb-16 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#0033ff]">
              04 — Impact
            </span>
            <h2 className="mt-6 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tighter text-[#f4f4f5] md:text-6xl">
              Measured outcomes.
            </h2>
          </div>
          <p className="max-w-xs font-mono text-xs leading-relaxed text-[#52525b]">
            Averaged across DHL, Advatix, Boston Scientific &amp; Dairy Farmers
            of America implementations.
          </p>
        </div>

        <div className="grid grid-cols-1 border-t border-l border-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {METRICS.map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              className="border-b border-r border-white/10 p-8 md:p-10"
              data-testid={`metric-${i}`}
            >
              <div className="font-display text-6xl font-extrabold tracking-tighter text-[#0033ff] md:text-7xl">
                <CountUp value={m.value} suffix={m.suffix} />
              </div>
              <div className="mt-6 font-display text-sm font-bold uppercase tracking-tight text-[#f4f4f5]">
                {m.label}
              </div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525b]">
                {m.note}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
